import React, { useState } from 'react';
import { MappedClass } from './InstructorProfile';
import { Edit2, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { deleteClass } from './profileAPIs';
import { EditClassForm } from './EditClass';

interface InactiveClassesProps {
    inactiveClasses: MappedClass[];
    onEdit?: (classData: MappedClass) => void;
    onDelete?: (classId: string) => void;
}

const InactiveClasses: React.FC<InactiveClassesProps> = ({ inactiveClasses, onEdit, onDelete }) => {
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [displayClasses, setDisplayClasses] = useState<MappedClass[]>(inactiveClasses);
    const [editingClass, setEditingClass] = useState<MappedClass | null>(null);

    // Update displayClasses when inactiveClasses prop changes
    React.useEffect(() => {
        setDisplayClasses(inactiveClasses);
    }, [inactiveClasses]);

    if (displayClasses.length === 0) {
        return null; // Don't render anything if there are no inactive classes
    }

    const handleDelete = async (classId: string) => {
        try {
            // Store the deleted class in case we need to restore it
            const classToDelete = displayClasses.find(cls => cls.id === classId);
            
            // Optimistically remove from display immediately
            setDisplayClasses(displayClasses.filter(cls => cls.id !== classId));
            
            // Call the delete API
            const response = await deleteClass(classId);
            
            // Narrow unknown response before accessing properties
            if (typeof response === 'object' && response !== null && 'success' in response) {
                const resp = response as { success: boolean; message?: string };
                if (resp.success) {
                    toast.success('Class deleted successfully');
                    // Call parent callback if provided
                    if (onDelete) {
                        onDelete(classId);
                    }
                    setDeleteConfirm(null);
                } else {
                    toast.error(resp.message || 'Failed to delete class');
                    // Restore the class if delete failed
                    if (classToDelete) {
                        setDisplayClasses(prev => [...prev, classToDelete]);
                    }
                }
            } else {
                toast.error('Unexpected response from delete API');
                // Restore the class if delete failed
                if (classToDelete) {
                    setDisplayClasses(prev => [...prev, classToDelete]);
                }
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to delete class');
            // Restore the class if delete failed
            const classToDelete = inactiveClasses.find(cls => cls.id === classId);
            if (classToDelete) {
                setDisplayClasses(prev => [...prev, classToDelete]);
            }
        }
    };

    const handleEdit = (classData: MappedClass) => {
        setEditingClass(classData);
        if (onEdit) {
            onEdit(classData);
        }
    };

    const getInactiveReason = (startDate: string, endDate: string): string => {
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Set time to midnight to compare dates only
        now.setHours(0, 0, 0, 0);
        
        if (now > end) {
            return "The class is finished.";
        }
        if (now < start) {
            return "The class has time to start.";
        }
        return "Inactive"; // A general status if neither of the above
    };

    return (
        <div className="mt-8 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Inactive Classes</h2>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Class Name
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Start Date
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                End Date
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Description
                            </th>
                            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {displayClasses.map((cls) => (
                            <tr key={cls.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                    {cls.title}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700">
                                    {cls.startDate}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700">
                                    {cls.endDate}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700">
                                    {getInactiveReason(cls.startDate, cls.endDate)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                    <div className="flex items-center justify-center gap-2">
                                        <button
                                            onClick={() => handleEdit(cls)}
                                            className="inline-flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors font-medium text-xs"
                                            title="Edit class"
                                        >
                                            <Edit2 size={16} /> Edit
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm(cls.id)}
                                            className="inline-flex items-center gap-1 px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors font-medium text-xs"
                                            title="Delete class"
                                        >
                                            <Trash2 size={16} /> Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-gray-900">Delete Class</h3>
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete this class? This action cannot be undone.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(deleteConfirm)}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Class Modal */}
            {editingClass && (
                <EditClassForm
                    show={true}
                    onClose={() => setEditingClass(null)}
                    cls={editingClass}
                />
            )}
        </div>
    );
}

export default InactiveClasses;
